import { useEffect, useMemo, useState } from "react";
import { Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import AmenityIconRenderer from "@/components/tpr/AmenityIconRenderer/AmenityIconRenderer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { defaultButtonClasses, secondaryButtonClasses } from "@/constants/ui";
import { OccurrenceAmenitiesError } from "@/api/remoteOccurrenceAmenitiesApi";
import { useLocationsQuery } from "@/hooks/useLocationsQuery";
import { useSetOccurrenceAmenities } from "@/hooks/useOccurrenceAmenities";
import type { Booking } from "@/types/Booking";
import type { OccurrenceAmenityClash } from "@/types/OccurrenceAmenities";
import { OccurrenceAmenityClashList } from "./OccurrenceAmenityClashList";

interface OccurrenceAmenitiesDialogProps {
  booking: Booking;
  dates: string[];
  /** The selected dates' shared amenity set, or null when they differ (nothing pre-ticked). */
  initialAmenityIds: string[] | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

/**
 * R27 — set the exact amenities for chosen dates of a repeating booking (elevated only). The
 * server checks every date and refuses the whole change on any clash; clashes render inline.
 */
export const OccurrenceAmenitiesDialog = ({ booking, dates, initialAmenityIds, isOpen, onOpenChange, onSaved }: OccurrenceAmenitiesDialogProps) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [clashes, setClashes] = useState<OccurrenceAmenityClash[]>([]);
  const setMutation = useSetOccurrenceAmenities(booking.id);

  const { data: locations } = useLocationsQuery({
    filters: { locationId: booking.locationId },
    enabled: isOpen && !!booking.locationId,
  });
  const location = locations?.find((l) => l.id === booking.locationId);
  const amenities = useMemo(
    () => (location?.amenities ?? []).filter((a) => a.isBookable && !a.hidden && !a.exclusions?.includes(booking.roomId)),
    [location, booking.roomId],
  );

  // Keyed on VALUE, not array identity: callers may derive the array per render, and a re-render
  // while open must not wipe the user's ticks or the inline clash list.
  const initialKey = initialAmenityIds === null ? null : [...initialAmenityIds].sort().join(",");
  useEffect(() => {
    if (isOpen) {
      setSelected(initialAmenityIds ?? []);
      setClashes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialKey]);

  const toggle = (id: string) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const handleSave = async () => {
    setClashes([]);
    try {
      // Only send what is offered: a pre-ticked series default that has since been hidden (or made
      // unbookable / excluded from the room) is invisible here, and the server would 400 every save.
      const offered = new Set(amenities.map((a) => a.id));
      await setMutation.mutateAsync({ dates, amenityIds: selected.filter((id) => offered.has(id)) });
      toast.success(`Amenities updated for ${dates.length} date${dates.length === 1 ? "" : "s"}`);
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      if (error instanceof OccurrenceAmenitiesError && error.clashes.length > 0) setClashes(error.clashes);
      else toast.error(error instanceof Error ? error.message : "Failed to update amenities");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="size-5" />
            Change amenities
          </DialogTitle>
          <DialogDescription>
            Applies to {dates.length} date{dates.length === 1 ? "" : "s"} only. The rest of the series keeps its usual amenities.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[360px] pr-4">
          <div className="space-y-3">
            {amenities.length === 0 && <p className="py-6 text-center text-sm text-gray-500">No bookable amenities at this location.</p>}
            {amenities.map((amenity) => (
              <div key={amenity.id} className="flex items-center space-x-3 rounded-lg border border-gray-200 p-3">
                <Checkbox id={`occ-amenity-${amenity.id}`} checked={selected.includes(amenity.id)} onCheckedChange={() => toggle(amenity.id)} />
                <Label htmlFor={`occ-amenity-${amenity.id}`} className="flex-1 cursor-pointer">
                  <AmenityIconRenderer amenity={amenity} withDescription />
                </Label>
              </div>
            ))}
          </div>
        </ScrollArea>

        <OccurrenceAmenityClashList clashes={clashes} />

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" className={secondaryButtonClasses} onClick={() => onOpenChange(false)} disabled={setMutation.isPending}>
            Cancel
          </Button>
          <Button type="button" className={defaultButtonClasses} onClick={handleSave} disabled={setMutation.isPending || !location}>
            {setMutation.isPending ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" /> Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
